import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FileDownload as FileDownloadIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface Response {
  id: number;
  item: ChecklistItem;
  user: User;
  instance: number | null;
  response_value: string;
  is_completed: boolean;
  cannot_complete: boolean;
  cannot_complete_reason: string;
  notes: string;
  uploaded_file: string | null;
  file_url: string | null;
  file_name: string | null;
  responded_at: string;
  updated_at: string;
}

interface Checklist {
  id: number;
  title: string;
  description: string;
  category: string;
  version: string;
  is_active: boolean;
  require_all_items: boolean;
  minimum_completion_percentage: number;
  created_by: User | null;
  created_at: string;
  updated_at: string;
}

interface ChecklistCompletion {
  id: number;
  checklist: Checklist;
  user: User;
  user_location: string | null;
  user_department: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  total_items: number;
  completed_items: number;
  completion_percentage: number;
  notes: string;
  responses: Response[];
  assigned_by: User | null;
  assignment_id: number | null;
  created_at: string;
  updated_at: string;
  audit_logs: Array<{
    id: number;
    action: string;
    actor: string;
    timestamp: string;
    details: string;
  }>;
}

const ChecklistCompletionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { completionId } = useParams<{ completionId: string }>();
  const [completion, setCompletion] = useState<ChecklistCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompletion = async () => {
      if (!completionId) return;
      
      try {
        setLoading(true);
        const response = await api.get(`/admin/checklist-completions/${completionId}`);
        setCompletion(response.data);
        setError(null);
      } catch (err: any) {
        setError('Failed to load completion details. Please try again.');
        console.error('Error fetching completion:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompletion();
  }, [completionId]);

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: any } = {
      completed: 'success',
      in_progress: 'info',
      not_started: 'default',
      overdue: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      completed: 'Completed',
      in_progress: 'In Progress',
      not_started: 'Not Started',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const renderResponseValue = (response: Response) => {
    if (response.cannot_complete) {
      return (
        <Box>
          <Chip label="Cannot Complete" color="warning" size="small" sx={{ mb: 1 }} />
          {response.cannot_complete_reason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {response.cannot_complete_reason}
            </Typography>
          )}
        </Box>
      );
    }

    if (response.file_url && response.file_name) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <FileDownloadIcon fontSize="small" />
          <Link href={response.file_url} target="_blank" rel="noopener noreferrer">
            {response.file_name}
          </Link>
        </Stack>
      );
    }

    if (response.response_value) {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {response.response_value}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No response provided
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !completion) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" component="h1">Completion Detail</Typography>
          <Button variant="outlined" onClick={() => navigate('/checklists/completions')}>
            Back to Completions
          </Button>
        </Stack>
        <Alert severity="error">{error || 'Completion not found'}</Alert>
      </Box>
    );
  }

  const attachments = completion.responses.filter(r => r.file_url && r.file_name);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">Completion Detail</Typography>
        <Button variant="outlined" onClick={() => navigate('/checklists/completions')}>
          Back to Completions
        </Button>
      </Stack>

      {/* Overview Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Checklist
              </Typography>
              <Typography variant="h6" gutterBottom>
                {completion.checklist.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {completion.checklist.description}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={completion.checklist.version} size="small" />
                <Chip label={completion.checklist.category} size="small" />
                {completion.checklist.is_active && (
                  <Chip label="Active" color="success" size="small" />
                )}
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Assignee
                      </Typography>
                      <Typography variant="body1">
                        {completion.user.first_name} {completion.user.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {completion.user.email}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                
                {completion.user_location && (
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocationIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body2">{completion.user_location}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                )}
                
                {completion.user_department && (
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Department
                        </Typography>
                        <Typography variant="body2">{completion.user_department}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Status and Progress */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={getStatusLabel(completion.status)}
                color={getStatusColor(completion.status) as any}
                size="medium"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Completion Progress
              </Typography>
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={completion.completion_percentage}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="body2">
                {completion.completed_items} / {completion.total_items} items ({completion.completion_percentage}%)
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Duration
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="body1">{formatDuration(completion.duration_seconds)}</Typography>
              </Stack>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Timestamps */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Started At
              </Typography>
              <Typography variant="body2">{formatDateTime(completion.started_at)}</Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Completed At
              </Typography>
              <Typography variant="body2">
                {formatDateTime(completion.completed_at) || 'Not completed'}
              </Typography>
            </Grid>
            
            {completion.assigned_by && (
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Assigned By
                </Typography>
                <Typography variant="body2">
                  {completion.assigned_by.first_name} {completion.assigned_by.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {completion.assigned_by.email}
                </Typography>
              </Grid>
            )}
          </Grid>
          
          {completion.notes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {completion.notes}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Responses Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Item Responses ({completion.responses.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {completion.responses.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No responses recorded yet.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Required</TableCell>
                  <TableCell>Response</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Responded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completion.responses.map((response) => (
                  <TableRow key={response.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {response.item.title}
                      </Typography>
                      {response.item.description && (
                        <Typography variant="caption" color="text.secondary">
                          {response.item.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={response.item.item_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {response.item.is_required ? (
                        <Chip label="Required" color="error" size="small" />
                      ) : (
                        <Chip label="Optional" size="small" />
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {renderResponseValue(response)}
                      {response.notes && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Notes: {response.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {response.is_completed ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Completed"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Not Completed"
                          color="default"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDateTime(response.responded_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Attachments Section */}
      {attachments.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Attachments ({attachments.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {attachments.map((response) => (
                <ListItem
                  key={response.id}
                  secondaryAction={
                    response.file_url ? (
                      <Button
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        href={response.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        component="a"
                      >
                        Download
                      </Button>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={response.file_name}
                    secondary={
                      <>
                        Item: {response.item.title}
                        <br />
                        Uploaded: {formatDateTime(response.responded_at)}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Audit Trail Section */}
      {completion.audit_logs && completion.audit_logs.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Audit Trail ({completion.audit_logs.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completion.audit_logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDateTime(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.action}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.actor}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {log.details || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Metadata Section */}
      <Card>
        <CardContent>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completion ID
                  </Typography>
                  <Typography variant="body2">{completion.id}</Typography>
                </Grid>
                
                {completion.assignment_id && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assignment ID
                    </Typography>
                    <Typography variant="body2">{completion.assignment_id}</Typography>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">{formatDateTime(completion.created_at)}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">{formatDateTime(completion.updated_at)}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Checklist Requirements
                  </Typography>
                  <Typography variant="body2">
                    {completion.checklist.require_all_items
                      ? 'All items required'
                      : `Minimum ${completion.checklist.minimum_completion_percentage}% completion`}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChecklistCompletionDetail;